import React from 'react';
import { motion } from 'framer-motion';
import { UserCog, Zap, ShoppingCart, Sparkles } from 'lucide-react';
export function Slide06Feature() {
  const pillars = [{
    icon: UserCog,
    title: 'Personalized',
    text: 'Learns your goals, budget, dietary needs, and past orders.'
  }, {
    icon: Zap,
    title: 'Effortless',
    text: 'A complete, balanced week is drafted the moment you open the app.'
  }, {
    icon: ShoppingCart,
    title: 'One-tap cart',
    text: 'Approve the week and everything lands in your cart, priced.'
  }];
  return <div className="w-full h-full flex items-center px-[clamp(4rem,8vw,10rem)] relative gap-12" data-id="element-163">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-[var(--surface)] -z-10 rounded-l-[100px] shadow-2xl shadow-[var(--fg)]/5" data-id="element-164" />

      <div className="flex-1 relative z-10" data-id="element-165">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} className="mb-12 max-w-xl" data-id="element-166">
          <div className="flex items-center gap-4 mb-6" data-id="element-167">
            <div className="h-px w-12 bg-[var(--accent)]" data-id="element-168" />
            <span className="text-[var(--accent)] font-semibold tracking-widest uppercase text-sm" data-id="element-169">
              The Proposed Feature
            </span>
          </div>
          <h2 className="font-display text-[clamp(3rem,4vw,4.5rem)] leading-[1.1] mb-6" data-id="element-170">
            Meet the{' '}
            <span className="text-[var(--accent)] italic" data-id="element-171">AI Meal Planner</span>
            .
          </h2>
          <p className="text-[clamp(1.15rem,1.25vw,1.5rem)] text-[var(--fg-muted)] leading-relaxed" data-id="element-172">
            An assistant that auto-builds your week around your goals, budget,
            and dietary needs — so the hardest decision becomes a single tap.
          </p>
        </motion.div>

        <div className="space-y-4 max-w-xl" data-id="element-173">
          {pillars.map((p, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2 + i * 0.15,
          type: 'spring',
          stiffness: 150,
          damping: 20
        }} className="bg-[var(--bg)] p-5 rounded-[2rem] shadow-sm border border-[var(--surface-alt)]/50 flex items-center gap-5 group hover:border-[var(--accent)]/30 transition-colors" data-id="element-174">
              <div className="w-14 h-14 bg-[var(--surface)] rounded-2xl flex items-center justify-center text-[var(--accent)] shrink-0 shadow-sm border border-[var(--surface-alt)] group-hover:scale-110 transition-transform" data-id="element-175">
                <p.icon size={24} strokeWidth={1.5} data-id="element-176" />
              </div>
              <div data-id="element-177">
                <h3 className="font-display text-2xl mb-1" data-id="element-178">{p.title}</h3>
                <p className="text-[var(--fg-muted)] leading-relaxed text-sm" data-id="element-179">
                  {p.text}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>

      <div className="flex-1 relative h-full flex items-center justify-center" data-id="element-180">
        <div className="relative w-full max-w-md aspect-square" data-id="element-181">
          {/* Background Card */}
          <motion.div animate={{
          y: [0, -10, 0]
        }} transition={{
          repeat: Infinity,
          duration: 5,
          ease: 'easeInOut',
          delay: 0.5
        }} className="absolute top-4 right-4 w-64 bg-white p-5 rounded-[2rem] shadow-xl border border-[var(--surface-alt)] rotate-[8deg]" data-id="element-182">
            <div className="flex items-center gap-3 mb-4" data-id="element-183">
              <div className="w-10 h-10 rounded-full bg-[var(--accent)]/10 flex items-center justify-center text-[var(--accent)]" data-id="element-184">
                <Zap size={20} data-id="element-185" />
              </div>
              <div className="font-semibold text-[var(--fg)]" data-id="element-186">
                Instant Draft
              </div>
            </div>
            <div className="space-y-3" data-id="element-187">
              <div className="h-2.5 w-full bg-[var(--surface-alt)] rounded-full" data-id="element-188" />
              <div className="h-2.5 w-4/5 bg-[var(--surface-alt)] rounded-full" data-id="element-189" />
              <div className="h-2.5 w-2/3 bg-[var(--surface-alt)] rounded-full" data-id="element-190" />
            </div>
          </motion.div>

          {/* Foreground Card */}
          <motion.div animate={{
          y: [0, -15, 0]
        }} transition={{
          repeat: Infinity,
          duration: 6,
          ease: 'easeInOut'
        }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 bg-[var(--fg)] text-[var(--bg)] p-8 rounded-[2.5rem] shadow-2xl z-10 border border-[var(--bg)]/10" data-id="element-191">
            <div className="flex justify-between items-start mb-8" data-id="element-192">
              <div className="w-14 h-14 rounded-2xl bg-[var(--bg)]/10 flex items-center justify-center text-[var(--accent)] shadow-inner" data-id="element-193">
                <UserCog size={28} data-id="element-194" />
              </div>
              <div className="px-3 py-1.5 bg-[var(--accent)] rounded-full text-[10px] font-bold uppercase tracking-wider text-white flex items-center gap-1" data-id="element-195">
                <Sparkles size={12} data-id="element-196" /> 98% Match
              </div>
            </div>
            <h4 className="font-display text-3xl mb-3" data-id="element-197">Dietary Match</h4>
            <p className="text-[var(--bg)]/70 text-base mb-8 leading-relaxed" data-id="element-198">
              All 7 meals perfectly align with your macro goals and preferences.
            </p>
            <button className="w-full py-4 bg-[var(--accent)] text-white rounded-xl font-semibold text-sm flex justify-center items-center gap-2 shadow-lg shadow-[var(--accent)]/20 hover:scale-[1.02] transition-transform" data-id="element-199">
              <ShoppingCart size={18} data-id="element-200" /> Add to Cart
            </button>
          </motion.div>
        </div>
      </div>
    </div>;
}